import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AuthFlag, Identity, LoginFailure, OnboardingState, PendingVerification, SecurityEvent, SecurityEventKind, Session } from '../types/auth';
import type { Role } from '../types/identity';
import { PRIVACY_VERSION, TERMS_VERSION, authFlags, seededIdentities } from '../data/auth';
import { type Throttle, describeDevice, digest, hashPassword, initialsFor, isLocked, judgePassword, judgeUsername, makeCode, makeSalt, makeToken, normaliseUsername, registerFailure, secondsRemaining, verifyPassword } from '../utils/auth';
const STORAGE_KEY = 'campuzon.auth.v1';
const SESSION_HOURS = 12;
const REMEMBERED_DAYS = 30;
interface RegisterInput {
  fullName: string;
  username: string;
  email: string;
  password: string;
  countryCode: string;
  marketingOptIn: boolean;
}
interface AuthValue {
  identity: Identity | null;
  authenticated: boolean;
  /** True only while the stored session is being restored on first paint. */
  restoring: boolean;
  flags: AuthFlag[];
  flagEnabled: (key: string) => boolean;
  usernameVerdict: (raw: string) => ReturnType<typeof judgeUsername>;
  passwordVerdict: (raw: string, context?: string[]) => ReturnType<typeof judgePassword>;
  register: (input: RegisterInput) => Promise<{
    ok: true;
    identity: Identity;
  } | {
    ok: false;
    error: string;
    field?: string;
  }>;
  login: (identifier: string, password: string, rememberDevice: boolean) => Promise<{
    ok: true;
    identity: Identity;
  } | {
    ok: false;
    reason: LoginFailure;
    message: string;
  }>;
  logout: () => void;
  logoutEverywhere: () => void;
  loginThrottle: Throttle;
  loginLockSeconds: number;

  /** Verification. The raw code is returned for the preview build only. */
  pending: PendingVerification | null;
  sendVerification: (channel: 'email' | 'phone', phone?: string) => {
    ok: boolean;
    error?: string;
  };
  confirmVerification: (code: string) => {
    ok: boolean;
    error?: string;
  };
  verificationCooldown: number;
  requestReset: (identifier: string) => {
    ok: true;
    code: string | null;
  } | {
    ok: false;
    error: string;
  };
  confirmReset: (code: string, newPassword: string) => {
    ok: true;
  } | {
    ok: false;
    error: string;
  };
  resetTarget: {
    masked: string;
    identityId: string;
  } | null;
  sessions: Session[];
  revokeSession: (id: string) => void;
  events: SecurityEvent[];

  /** Sensitive actions re-check the password rather than trusting the session. */
  reauthenticate: (password: string) => boolean;
  updateOnboarding: (patch: Partial<OnboardingState>) => void;
  updateProfile: (patch: Partial<Identity>) => void;
  changeUsername: (raw: string, password: string) => {
    ok: true;
  } | {
    ok: false;
    error: string;
  };
  switchRole: (role: Role) => boolean;
  deactivate: () => void;

  /** Where to return after signing in, captured by the route guard. */
  returnTo: string | null;
  setReturnTo: (path: string | null) => void;
}
const AuthContext = createContext<AuthValue | null>(null);
function now(): string {
  return new Date().toISOString();
}
function hoursFromNow(hours: number): string {
  return new Date(Date.now() + hours * 3600_000).toISOString();
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [identities, setIdentities] = useState<Identity[]>(seededIdentities);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [pending, setPending] = useState<PendingVerification | null>(null);
  const [resetState, setResetState] = useState<PendingVerification | null>(null);
  const [loginThrottle, setLoginThrottle] = useState<Throttle>({
    attempts: 0,
    lockedUntil: null
  });
  const [cooldown, setCooldown] = useState(0);
  const [tick, setTick] = useState(0);
  const [returnTo, setReturnTo] = useState<string | null>(null);

  /* Restore a stored session. An expired one is discarded, not silently kept. */
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          identityId: string;
          expiresAt: string;
        };
        if (new Date(parsed.expiresAt).getTime() > Date.now()) {
          setCurrentId(parsed.identityId);
          setSessions([{
            id: makeToken('sess'),
            identityId: parsed.identityId,
            device: describeDevice(),
            location: 'Lagos, Nigeria',
            createdAt: now(),
            lastSeenAt: now(),
            expiresAt: parsed.expiresAt,
            current: true,
            rememberDevice: true
          }, {
            id: makeToken('sess'),
            identityId: parsed.identityId,
            device: 'Chrome on Android',
            location: 'Akure, Nigeria',
            createdAt: '2026-08-09T19:22:00Z',
            lastSeenAt: '2026-08-12T08:04:00Z',
            expiresAt: hoursFromNow(200),
            current: false,
            rememberDevice: true
          }]);
        } else {
          window.localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch {
      /* A malformed stored session is treated as no session. */
    }
    setRestoring(false);
  }, []);

  /* Drives lock countdowns and resend cooldowns. */
  useEffect(() => {
    if (cooldown === 0 && loginThrottle.lockedUntil === null) return;
    const timer = window.setInterval(() => {
      setCooldown(value => value > 0 ? value - 1 : 0);
      setTick(value => value + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown, loginThrottle.lockedUntil]);
  const identity = useMemo(() => identities.find(item => item.id === currentId) ?? null, [identities, currentId]);
  const takenKeys = useMemo(() => identities.map(item => item.usernameKey), [identities]);
  const record = useCallback((identityId: string, kind: SecurityEventKind, detail: string) => {
    setEvents(current => [{
      id: makeToken('sec'),
      identityId,
      kind,
      detail,
      at: now(),
      device: describeDevice()
    }, ...current]);
  }, []);
  const persist = useCallback((identityId: string, remember: boolean) => {
    const expiresAt = remember ? hoursFromNow(REMEMBERED_DAYS * 24) : hoursFromNow(SESSION_HOURS);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      identityId,
      expiresAt
    }));
    return expiresAt;
  }, []);
  const patchIdentity = useCallback((identityId: string, patch: Partial<Identity>) => {
    setIdentities(current => current.map(item => item.id === identityId ? {
      ...item,
      ...patch
    } : item));
  }, []);

  /* ------------------------------------------------------------- registration */

  const register = useCallback<AuthValue['register']>(async input => {
    await new Promise(resolve => window.setTimeout(resolve, 450));
    const verdict = judgeUsername(input.username, takenKeys);
    if (!verdict.ok) return {
      ok: false,
      error: verdict.message,
      field: 'username'
    };
    const email = input.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      return {
        ok: false,
        error: 'Enter a valid email address.',
        field: 'email'
      };
    }
    /* Uniqueness is enforced, but the response never confirms which existing
       address it collided with — that would enumerate accounts. */
    if (identities.some(item => item.email.toLowerCase() === email)) {
      return {
        ok: false,
        error: 'We could not create an account with those details. Try signing in instead.',
        field: 'email'
      };
    }
    const password = judgePassword(input.password, [input.username, input.fullName, email]);
    if (password.problems.length > 0) {
      return {
        ok: false,
        error: password.problems[0],
        field: 'password'
      };
    }
    const {
      hash,
      salt
    } = hashPassword(input.password);
    const key = normaliseUsername(input.username);
    const created: Identity = {
      id: `CAMP-${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
      username: key,
      usernameKey: key,
      usernameChangedAt: null,
      usernameHistory: [],
      displayName: input.fullName.trim(),
      email,
      emailState: 'unverified',
      phone: null,
      phoneState: 'unverified',
      passwordHash: hash,
      passwordSalt: salt,
      passwordUpdatedAt: now(),
      status: 'pending_verification',
      statusReason: null,
      roles: ['student'],
      activeRole: 'student',
      countryCode: input.countryCode,
      institutionId: null,
      campusId: null,
      programme: null,
      level: null,
      interests: [],
      photoInitials: initialsFor(input.fullName),
      createdAt: now(),
      lastLoginAt: now(),
      consent: {
        termsVersion: TERMS_VERSION,
        privacyVersion: PRIVACY_VERSION,
        acceptedAt: now(),
        marketingOptIn: input.marketingOptIn
      },
      onboarding: {
        usernameConfirmed: true,
        photoAdded: false,
        campusSelected: false,
        programmeAdded: false,
        interestsAdded: false,
        notificationsSet: false,
        dismissed: false
      },
      seeded: false
    };
    setIdentities(current => [...current, created]);
    setCurrentId(created.id);
    const expiresAt = persist(created.id, false);
    setSessions([{
      id: makeToken('sess'),
      identityId: created.id,
      device: describeDevice(),
      location: 'Current device',
      createdAt: now(),
      lastSeenAt: now(),
      expiresAt,
      current: true,
      rememberDevice: false
    }]);
    record(created.id, 'account_created', `Account created as @${created.username}`);
    record(created.id, 'consent_recorded', `Terms ${TERMS_VERSION} and privacy ${PRIVACY_VERSION} accepted`);

    /* Issue the first verification code immediately. */
    const code = makeCode();
    setPending({
      identityId: created.id,
      channel: 'email',
      code,
      codeHash: digest(code, created.passwordSalt),
      expiresAt: Date.now() + 15 * 60_000,
      attempts: 0,
      consumed: false
    });
    setCooldown(30);
    return {
      ok: true,
      identity: created
    };
  }, [identities, takenKeys, persist, record]);

  /* -------------------------------------------------------------------- login */

  const login = useCallback<AuthValue['login']>(async (identifier, password, rememberDevice) => {
    if (isLocked(loginThrottle)) {
      return {
        ok: false,
        reason: 'rate_limited',
        message: `Too many attempts. Try again in ${secondsRemaining(loginThrottle)} seconds.`
      };
    }
    await new Promise(resolve => window.setTimeout(resolve, 450));
    const needle = identifier.trim().toLowerCase().replace(/^@/, '');
    const found = identities.find(item => item.usernameKey === needle || item.email.toLowerCase() === needle || (item.phone ?? '').replace(/\D/g, '') === needle.replace(/\D/g, ''));

    /* An unknown identifier and a wrong password give the SAME message, so a
       failed attempt cannot be used to discover whether an account exists. */
    const generic = 'Those details do not match an account. Check your username, email or password.';
    if (!found) {
      setLoginThrottle(current => registerFailure(current));
      return {
        ok: false,
        reason: 'unknown_identifier',
        message: generic
      };
    }
    if (!verifyPassword(password, found.passwordHash, found.passwordSalt)) {
      setLoginThrottle(current => registerFailure(current));
      record(found.id, 'login_failed', 'Incorrect password');
      return {
        ok: false,
        reason: 'wrong_password',
        message: generic
      };
    }
    if (found.status === 'suspended') {
      record(found.id, 'login_failed', 'Blocked: account suspended');
      return {
        ok: false,
        reason: 'suspended',
        message: 'This account is suspended. Contact Campuzon support — they can explain and take an appeal.'
      };
    }
    if (found.status === 'deactivated') {
      return {
        ok: false,
        reason: 'deactivated',
        message: 'This account is deactivated. Contact support to reactivate it.'
      };
    }
    setLoginThrottle({
      attempts: 0,
      lockedUntil: null
    });
    setCurrentId(found.id);
    patchIdentity(found.id, {
      lastLoginAt: now()
    });
    const expiresAt = persist(found.id, rememberDevice);
    setSessions([{
      id: makeToken('sess'),
      identityId: found.id,
      device: describeDevice(),
      location: 'Current device',
      createdAt: now(),
      lastSeenAt: now(),
      expiresAt,
      current: true,
      rememberDevice
    }, ...sessions.filter(item => item.identityId === found.id && !item.current)]);
    record(found.id, 'login', `Signed in${rememberDevice ? ' — device remembered' : ''}`);

    /* An unverified account signs in but stays limited, rather than being
       locked out of the product it just created. */
    return {
      ok: true,
      identity: {
        ...found,
        lastLoginAt: now()
      }
    };
  }, [identities, loginThrottle, persist, record, patchIdentity, sessions]);
  const logout = useCallback(() => {
    if (identity) record(identity.id, 'logout', 'Signed out of this session');
    window.localStorage.removeItem(STORAGE_KEY);
    setCurrentId(null);
    setSessions([]);
    setPending(null);
    setReturnTo(null);
  }, [identity, record]);
  const logoutEverywhere = useCallback(() => {
    if (identity) record(identity.id, 'logout_all', 'Signed out of every device');
    window.localStorage.removeItem(STORAGE_KEY);
    setCurrentId(null);
    setSessions([]);
    setReturnTo(null);
  }, [identity, record]);

  /* ------------------------------------------------------------ verification */

  const sendVerification = useCallback<AuthValue['sendVerification']>((channel, phone) => {
    if (!identity) return {
      ok: false,
      error: 'Sign in first.'
    };
    if (cooldown > 0) {
      return {
        ok: false,
        error: `Wait ${cooldown} seconds before requesting another code.`
      };
    }
    if (channel === 'phone' && !phone && !identity.phone) {
      return {
        ok: false,
        error: 'Add a phone number first.'
      };
    }
    const code = makeCode();
    setPending({
      identityId: identity.id,
      channel,
      code,
      codeHash: digest(code, identity.passwordSalt),
      expiresAt: Date.now() + 15 * 60_000,
      attempts: 0,
      consumed: false
    });
    if (channel === 'phone' && phone) {
      patchIdentity(identity.id, {
        phone,
        phoneState: 'pending'
      });
    }
    setCooldown(30);
    return {
      ok: true
    };
  }, [identity, cooldown, patchIdentity]);
  const confirmVerification = useCallback<AuthValue['confirmVerification']>(code => {
    if (!pending || !identity) return {
      ok: false,
      error: 'Request a code first.'
    };
    if (pending.consumed) return {
      ok: false,
      error: 'That code has already been used.'
    };
    if (Date.now() > pending.expiresAt) {
      return {
        ok: false,
        error: 'That code has expired. Request a new one.'
      };
    }
    if (pending.attempts >= 5) {
      return {
        ok: false,
        error: 'Too many incorrect codes. Request a new one.'
      };
    }
    if (digest(code.trim(), identity.passwordSalt) !== pending.codeHash) {
      setPending({
        ...pending,
        attempts: pending.attempts + 1
      });
      return {
        ok: false,
        error: `That code is not correct. ${4 - pending.attempts} attempts left.`
      };
    }
    setPending({
      ...pending,
      consumed: true
    });
    if (pending.channel === 'email') {
      patchIdentity(identity.id, {
        emailState: 'verified',
        status: identity.status === 'pending_verification' ? 'active' : identity.status
      });
      record(identity.id, 'email_verified', `Email verified`);
    } else {
      patchIdentity(identity.id, {
        phoneState: 'verified'
      });
      record(identity.id, 'phone_verified', 'Phone number verified');
    }
    return {
      ok: true
    };
  }, [pending, identity, patchIdentity, record]);

  /* ---------------------------------------------------------------- recovery */

  const requestReset = useCallback<AuthValue['requestReset']>(identifier => {
    const needle = identifier.trim().toLowerCase().replace(/^@/, '');
    const found = identities.find(item => item.usernameKey === needle || item.email.toLowerCase() === needle);
    /* Always report success. Telling a stranger that an address is unknown
       would confirm which addresses have accounts. */
    if (!found) return {
      ok: true,
      code: null
    };
    const code = makeCode();
    setResetState({
      identityId: found.id,
      channel: 'email',
      code,
      codeHash: digest(code, found.passwordSalt),
      expiresAt: Date.now() + 10 * 60_000,
      attempts: 0,
      consumed: false
    });
    return {
      ok: true,
      code
    };
  }, [identities]);
  const confirmReset = useCallback<AuthValue['confirmReset']>((code, newPassword) => {
    if (!resetState) return {
      ok: false,
      error: 'Start the recovery process again.'
    };
    if (resetState.consumed) {
      return {
        ok: false,
        error: 'That recovery code has already been used.'
      };
    }
    if (Date.now() > resetState.expiresAt) {
      return {
        ok: false,
        error: 'That recovery code has expired. Request a new one.'
      };
    }
    const target = identities.find(item => item.id === resetState.identityId);
    if (!target) return {
      ok: false,
      error: 'Start the recovery process again.'
    };
    if (resetState.attempts >= 5) {
      return {
        ok: false,
        error: 'Too many incorrect codes. Request a new one.'
      };
    }
    if (digest(code.trim(), target.passwordSalt) !== resetState.codeHash) {
      setResetState({
        ...resetState,
        attempts: resetState.attempts + 1
      });
      return {
        ok: false,
        error: `That code is not correct. ${4 - resetState.attempts} attempts left.`
      };
    }
    const verdict = judgePassword(newPassword, [target.username, target.displayName, target.email]);
    if (verdict.problems.length > 0) return {
      ok: false,
      error: verdict.problems[0]
    };
    const salt = makeSalt();
    patchIdentity(target.id, {
      passwordHash: digest(newPassword, salt),
      passwordSalt: salt,
      passwordUpdatedAt: now()
    });
    /* Single use, and every other session is invalidated. */
    setResetState({
      ...resetState,
      consumed: true
    });
    setSessions(current => current.filter(item => item.identityId !== target.id));
    window.localStorage.removeItem(STORAGE_KEY);
    setCurrentId(null);
    record(target.id, 'password_reset', 'Password reset — all other sessions signed out');
    return {
      ok: true
    };
  }, [resetState, identities, patchIdentity, record]);
  const resetTarget = useMemo(() => {
    if (!resetState) return null;
    const target = identities.find(item => item.id === resetState.identityId);
    if (!target) return null;
    return {
      masked: target.email,
      identityId: target.id
    };
  }, [resetState, identities]);

  /* ------------------------------------------------------------------ profile */

  const reauthenticate = useCallback((password: string) => {
    if (!identity) return false;
    const ok = verifyPassword(password, identity.passwordHash, identity.passwordSalt);
    if (ok) record(identity.id, 'reauthenticated', 'Confirmed password for a sensitive action');
    return ok;
  }, [identity, record]);
  const updateOnboarding = useCallback<AuthValue['updateOnboarding']>(patch => {
    if (!identity) return;
    const next = {
      ...identity.onboarding,
      ...patch
    };
    patchIdentity(identity.id, {
      onboarding: next
    });
    const complete = next.usernameConfirmed && next.campusSelected && next.programmeAdded && next.interestsAdded && next.notificationsSet;
    if (complete && !identity.onboarding.notificationsSet) {
      record(identity.id, 'onboarding_completed', 'Onboarding completed');
    }
  }, [identity, patchIdentity, record]);
  const updateProfile = useCallback<AuthValue['updateProfile']>(patch => {
    if (!identity) return;
    patchIdentity(identity.id, patch);
  }, [identity, patchIdentity]);
  const changeUsername = useCallback<AuthValue['changeUsername']>((raw, password) => {
    if (!identity) return {
      ok: false,
      error: 'Sign in first.'
    };
    if (!verifyPassword(password, identity.passwordHash, identity.passwordSalt)) {
      return {
        ok: false,
        error: 'That password is not correct.'
      };
    }
    const verdict = judgeUsername(raw, takenKeys.filter(key => key !== identity.usernameKey));
    if (!verdict.ok) return {
      ok: false,
      error: verdict.message
    };
    if (identity.usernameChangedAt) {
      const days = (Date.now() - new Date(identity.usernameChangedAt).getTime()) / 86_400_000;
      if (days < 30) {
        return {
          ok: false,
          error: `Usernames can change once every 30 days. Try again in ${Math.ceil(30 - days)} days.`
        };
      }
    }
    const key = normaliseUsername(raw);
    patchIdentity(identity.id, {
      username: key,
      usernameKey: key,
      usernameChangedAt: now(),
      /* The old name is retained so historical records stay resolvable and
         nobody can immediately claim it and impersonate you. */
      usernameHistory: [identity.username, ...identity.usernameHistory]
    });
    record(identity.id, 'username_changed', `@${identity.username} → @${key}`);
    return {
      ok: true
    };
  }, [identity, takenKeys, patchIdentity, record]);
  const switchRole = useCallback((role: Role) => {
    if (!identity) return false;
    /* Authorisation is checked against the roles held, not requested. */
    if (!identity.roles.includes(role)) return false;
    patchIdentity(identity.id, {
      activeRole: role
    });
    record(identity.id, 'role_switched', `Active role set to ${role}`);
    return true;
  }, [identity, patchIdentity, record]);
  const revokeSession = useCallback((id: string) => {
    setSessions(current => current.filter(item => item.id !== id));
    if (identity) record(identity.id, 'session_revoked', 'A session was signed out remotely');
  }, [identity, record]);
  const deactivate = useCallback(() => {
    if (!identity) return;
    patchIdentity(identity.id, {
      status: 'deactivated'
    });
    window.localStorage.removeItem(STORAGE_KEY);
    setCurrentId(null);
    setSessions([]);
  }, [identity, patchIdentity]);
  const flagEnabled = useCallback((key: string) => authFlags.find(flag => flag.key === key)?.enabled ?? false, []);
  const value: AuthValue = {
    identity,
    authenticated: identity !== null,
    restoring,
    flags: authFlags,
    flagEnabled,
    usernameVerdict: raw => judgeUsername(raw, takenKeys),
    passwordVerdict: (raw, context) => judgePassword(raw, context),
    register,
    login,
    logout,
    logoutEverywhere,
    loginThrottle,
    loginLockSeconds: secondsRemaining(loginThrottle),
    pending,
    sendVerification,
    confirmVerification,
    verificationCooldown: cooldown,
    requestReset,
    confirmReset,
    resetTarget,
    sessions: sessions.filter(item => item.identityId === currentId),
    revokeSession,
    events: events.filter(item => item.identityId === currentId),
    reauthenticate,
    updateOnboarding,
    updateProfile,
    changeUsername,
    switchRole,
    deactivate,
    returnTo,
    setReturnTo
  };

  /* `tick` keeps countdown consumers re-rendering while a lock is active. */
  void tick;
  return <AuthContext.Provider value={value} data-id="element-5528" data-mp="16dpb52ojccio">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}